// ---------------------------------------------------------------------------
// Billing Records Retention — BIL-022 Phase 1 (constants + vocabulary)
// ---------------------------------------------------------------------------
// Story BIL-022: "Billing records support export/retention/erasure policy
// while preserving legal accounting records."
//
// Per docs/vision_execution/openclaw/BIL-022-DECISION-PACKET.md the gap is
// NOT a missing table — the existing `retention_policies` row in
// `packages/db/src/schema/gdpr.ts` already supports
// `(tenant_id, data_category, applies_to_table, retention_days, legal_basis)`.
// The gap is:
//
//   (1) no canonical `billing_records` data-category constant
//   (2) no jurisdiction → retention-days vocabulary (EU 10y / US 7y / RO 5y)
//   (3) no closed retention-basis vocabulary the future CQRS event
//       `billing.erasure_carve_out_applied@v1` will reference
//   (4) no list of the four billing tables that get seeded per-tenant
//       on first invoice (invoices / payments / commission_ledger /
//       stripe_customers — `billing_events` stays infinite)
//
// Phase 1 scope (this file): pure constant vocabulary + jurisdiction
// resolver. The DSAR provider entry (`createBillingRecordsDsarProvider`),
// the retention seed function, the erasure handler with carve-out, and the
// CQRS event emission land in later phases governed by the decision
// packet.
//
// Why a `db` package file rather than `platform` — these constants are
// referenced both by the future erasure-handler in `@ease/platform` and
// by the future migration seed that writes default rows into
// `retention_policies` on tenant create. Keeping them next to the schema
// keeps the source of truth single.
// ---------------------------------------------------------------------------

/**
 * Single canonical data-category string the future
 * `createBillingRecordsDsarProvider` + retention seed + erasure handler
 * will all reference. Any drift between them must fail loudly — that is
 * what the schema-pin test enforces.
 */
export const BILLING_RECORDS_CATEGORY = "billing_records" as const;

export type BillingRecordsCategory = typeof BILLING_RECORDS_CATEGORY;

/**
 * Closed retention-basis vocabulary keyed by jurisdiction. The payload of
 * the future `billing.erasure_carve_out_applied@v1` CQRS event carries the
 * basis string verbatim so auditors can replay which legal regime gated a
 * given row's retention.
 *
 * Source: BIL-022-DECISION-PACKET §3 (Retention policy seed + jurisdiction
 * column).
 *
 *   - EU-10y: DE §147 AO worst-case member-state, EU OSS default
 *   - US-7y:  US default for federal income-tax records
 *   - RO-5y:  RO Codul Fiscal (Romanian tax code, 5y for tax records)
 *   - FR-6y:  FR Art. L102 B LPF
 *   - INFINITE: immutable CQRS log (billing_events table)
 *
 * The packet enumerates EU/US/RO as the V1 set; FR is listed in the
 * packet preamble (Art. L102 B LPF: 6y) so we include it as a
 * jurisdiction-specific shortcut rather than fold it into the EU default.
 */
export const BILLING_RECORDS_RETENTION_BASES = [
  "EU-10y",
  "US-7y",
  "RO-5y",
  "FR-6y",
  "INFINITE",
] as const;

export type BillingRecordsRetentionBasis =
  (typeof BILLING_RECORDS_RETENTION_BASES)[number];

/**
 * Canonical retention-days mapping. Single source of truth — the future
 * retention seed function MUST resolve via `getBillingRetentionDays(basis)`
 * rather than re-hardcoding the number in a migration.
 *
 * `INFINITE` is represented as `null` (no purge) per the packet's
 * "billing_events: infinite (immutable CQRS log)" row.
 */
export const BILLING_RECORDS_RETENTION_DAYS: Record<
  BillingRecordsRetentionBasis,
  number | null
> = {
  // 10 years; 3653 = 10 * 365 + 3 leap-day allowance per packet
  "EU-10y": 3653,
  // 7 years
  "US-7y": 2557,
  // 5 years
  "RO-5y": 1826,
  // 6 years
  "FR-6y": 2191,
  INFINITE: null,
};

/**
 * Canonical billing-table inventory that gets seeded a `retention_policies`
 * row per tenant on first invoice. `billing_events` is INTENTIONALLY excluded
 * because it is an immutable CQRS log (packet row "billing_events: infinite").
 *
 * The retention cron walks these table names by category — any future
 * billing table that joins the personal-data graph (e.g. a future
 * `refund_ledger` from BIL-010) MUST add itself to this list AND to the
 * jurisdiction-default seed before its data starts retaining unboundedly.
 */
export const BILLING_RECORDS_TABLES = [
  "invoices",
  "payments",
  "commission_ledger",
  "stripe_customers",
] as const;

export type BillingRecordsTable = (typeof BILLING_RECORDS_TABLES)[number];

/**
 * ISO-3166 alpha-2 (or alpha-2 + region) → retention basis. The future
 * tenant-create handler reads `billing_jurisdiction` off the tenant row
 * and calls this resolver to pick the seed basis. Unknown jurisdictions
 * fall through to EU-10y — the strictest member-state — so the platform
 * defaults to over-retention (legal) rather than under-retention
 * (illegal).
 */
export function resolveBillingRetentionBasis(
  jurisdiction: string | null | undefined,
): BillingRecordsRetentionBasis {
  if (!jurisdiction) {
    return "EU-10y";
  }
  const normalized = jurisdiction.trim().toUpperCase();
  if (normalized === "RO") {
    return "RO-5y";
  }
  if (normalized === "US") {
    return "US-7y";
  }
  if (normalized === "FR") {
    return "FR-6y";
  }
  // EU member states + default fallback all roll up to the strictest
  // member-state requirement.
  return "EU-10y";
}

/**
 * Convenience helper: jurisdiction string → retention days. Returns `null`
 * for the INFINITE basis (no purge). The retention cron call site is the
 * primary consumer.
 */
export function getBillingRetentionDays(
  basis: BillingRecordsRetentionBasis,
): number | null {
  return BILLING_RECORDS_RETENTION_DAYS[basis];
}
